import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { MdArrowBack } from 'react-icons/md';
import { useParams, useLocation, useNavigate, Navigate } from 'react-router-dom';
import { type ApprovalMode } from '@utils/approvalMode';
import { peekPendingUploadFiles, clearPendingUploadFiles } from '@utils/pendingFiles';
import Chat from '@components/Chat';
import useConversations from '@contexts/useConversations';
import type { ConversationAppRun } from '@contexts/ConversationsContextStore';
import { useChat, type Message } from '@hooks/useChat';
import Skeleton from '@components/1-atoms/Skeleton';
import {
  loadExampleAttachments,
  type ExamplePromptAttachment,
} from '@components/TaskPrompt/examplePrompts';
import { appRunPath } from './Apps/appRunPath';
import {
  AppRunReturn,
  AppRunReturnInner,
  BackToApp,
  PageContainer,
  SkeletonContainer,
} from './Conversation.styled';

interface LocationState {
  initialMessage?: string;
  hasFiles?: boolean;
  attachmentUrls?: ExamplePromptAttachment[];
  runnerType?: string;
  approvalMode?: ApprovalMode;
  /** Set when opened from an app run's Transcript button. */
  appRun?: ConversationAppRun;
}

const ConversationPage: React.FC = () => {
  const { conversationId: urlConversationId } = useParams<{ conversationId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const intl = useIntl();
  const { conversations } = useConversations();

  const locationState = location.state as LocationState | null;
  // The sidebar's list stands in for a pasted link or a new tab, where nothing
  // was passed along.
  const appRun =
    locationState?.appRun ??
    conversations.find((entry) => entry.conversationId === urlConversationId)?.appRun ??
    null;
  const initialMessage = locationState?.initialMessage;
  const hasFiles = locationState?.hasFiles;
  const attachmentUrls = locationState?.attachmentUrls;
  const initialRunnerType = locationState?.runnerType;
  const initialApprovalMode = locationState?.approvalMode;

  // File objects can't survive React Router v7 state (JSON-serialized).
  // They're stored in a module-level ref.  Read non-destructively here
  // (safe under StrictMode double-render) and clear in the send effect.
  const [initialFiles] = useState(() => peekPendingUploadFiles());

  const [exampleLoadError, setExampleLoadError] = useState<string | null>(null);

  // The route `/conversation/:conversationId` reuses this component instance
  // when the user switches conversations (e.g. via the sidebar). Drop any
  // example-attachment error from a previous /conversation/new visit so it
  // doesn't render on top of an unrelated conversation. React docs' canonical
  // "adjusting state when a prop changes" pattern (state-tracker, not ref):
  // setState during render is OK — React discards the partial render.
  const [prevConversationId, setPrevConversationId] = useState(urlConversationId);
  if (prevConversationId !== urlConversationId) {
    setPrevConversationId(urlConversationId);
    setExampleLoadError(null);
  }

  const isNewConversation = urlConversationId === 'new';

  // For new conversations pass null; for existing ones pass the real ID
  const chatConversationId = isNewConversation ? null : (urlConversationId || null);

  const {
    messages,
    isLoadingMessages,
    isSending,
    isReconnecting,
    error,
    resolvedConversationId,
    sendMessage,
    cancelRequest,
    pendingModelSuggestion,
    acceptModelSuggestion,
    dismissModelSuggestion,
    pendingAskUser,
    submitAskUser,
    skipAskUser,
    approveToolExecution,
    rejectToolExecution,
    submitFeedback,
    deleteFeedback,
    retryAfterInterruption,
    setupSteps,
    setupExiting,
    compacting,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChat(chatConversationId);

  // Auto-send initial message for new conversations (once).
  // The ref prevents double-send in React StrictMode.
  const hasSentRef = useRef(false);
  useEffect(() => {
    if (!isNewConversation || !initialMessage || hasSentRef.current) return;
    hasSentRef.current = true;
    (async () => {
      try {
        // Example prompts pass attachment URLs through location state so the
        // home-page click is instant; we resolve them to File objects here,
        // during the optimistic-message + thinking-indicator window.
        const files = initialFiles
          ?? (attachmentUrls?.length ? await loadExampleAttachments(attachmentUrls) : undefined);
        await sendMessage(
          initialMessage, files, undefined,
          initialRunnerType, initialApprovalMode,
        );
        clearPendingUploadFiles();
      } catch (err) {
        setExampleLoadError(err instanceof Error ? err.message : 'Failed to load example attachment');
      }
    })();
  }, [
    isNewConversation, initialMessage, initialFiles, attachmentUrls,
    initialRunnerType, initialApprovalMode, sendMessage,
  ]);

  // When SSE resolves a real conversation ID, update the URL
  useEffect(() => {
    if (resolvedConversationId && isNewConversation) {
      navigate(`/conversation/${resolvedConversationId}`, { replace: true });
    }
  }, [resolvedConversationId, isNewConversation, navigate]);

  // For new conversations the React Query cache is empty until the first SSE
  // event resolves a real conversation ID. Show the user's own message
  // immediately so the chat doesn't look blank while the agent responds.
  const optimisticInitialMessage = useMemo<Message | null>(() => {
    if (!isNewConversation || !initialMessage) return null;
    return {
      id: 'optimistic-initial-user-message',
      role: 'user',
      content: initialMessage,
      timestamp: new Date(),
      attachments: undefined,
    };
  }, [isNewConversation, initialMessage]);

  const displayMessages = useMemo<Message[]>(() => {
    // Once the hook has any real messages (either from SSE or the resolved
    // conversation query), drop the placeholder — it will be in there already.
    if (!optimisticInitialMessage || messages.length > 0) return messages;
    return [optimisticInitialMessage];
  }, [optimisticInitialMessage, messages]);

  // If someone refreshes /conversation/new without route state, redirect home.
  // initialMessage comes from location.state which is null after a page refresh.
  if (isNewConversation && !initialMessage && !hasFiles && !attachmentUrls?.length) {
    return <Navigate to="/" replace />;
  }

  // Skeleton loading state for existing conversations
  if (isLoadingMessages && !isNewConversation) {
    return (
      <PageContainer>
        <SkeletonContainer>
          <Skeleton
            withIcon
            withLine
            height={56}
            dataTestId="conversation-skeleton-1"
          />
          <Skeleton
            withIcon={false}
            withLine
            height={80}
            dataTestId="conversation-skeleton-2"
          />
          <Skeleton
            withIcon
            withLine
            height={56}
            dataTestId="conversation-skeleton-3"
          />
          <Skeleton
            withIcon={false}
            withLine
            height={120}
            dataTestId="conversation-skeleton-4"
          />
        </SkeletonContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {appRun && (
        <AppRunReturn>
          <AppRunReturnInner>
            <BackToApp to={appRunPath(appRun)} data-testid="conversation__back-to-app">
              <MdArrowBack size={14} aria-hidden="true" />
              {intl.formatMessage(
                { id: 'conversation.backToApp', defaultMessage: 'Back to {app}' },
                {
                  app: intl.formatMessage({
                    id: appRun.nameKey,
                    defaultMessage: appRun.nameDefault,
                  }),
                },
              )}
            </BackToApp>
          </AppRunReturnInner>
        </AppRunReturn>
      )}
      <Chat
        messages={displayMessages}
        isLoading={isSending}
        isReconnecting={isReconnecting}
        error={exampleLoadError ?? error}
        sendMessage={sendMessage}
        onCancel={cancelRequest}
        onLoadMore={fetchNextPage}
        hasMoreMessages={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        pendingModelSuggestion={pendingModelSuggestion}
        onAcceptModelSuggestion={acceptModelSuggestion}
        onDismissModelSuggestion={dismissModelSuggestion}
        pendingAskUser={pendingAskUser}
        onSubmitAskUser={submitAskUser}
        onSkipAskUser={skipAskUser}
        onApproveToolExecution={approveToolExecution}
        onRejectToolExecution={rejectToolExecution}
        onSubmitFeedback={submitFeedback}
        onDeleteFeedback={deleteFeedback}
        onRetryAfterInterruption={retryAfterInterruption}
        setupSteps={setupSteps}
        setupExiting={setupExiting}
        compacting={compacting}
        initialRunnerType={initialRunnerType}
        conversationId={resolvedConversationId || chatConversationId}
      />
    </PageContainer>
  );
};

export default ConversationPage;
